// src/components/gavion/meetings/SuggestedTasks.tsx
// Suggested tasks (spec 07 §6): parsed action items, all pre-selected; nothing is created without a click.
import { useState } from "react";
import { useNotify, useRefresh, useTranslate, type Identifier } from "ra-core";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { parseActionItems } from "@/components/gavion/domain/granola";
import { errorCodeOf } from "../integrations/api/types";
import { useIntegrationApi } from "../integrations/api/useIntegrationApi";

export const SuggestedTasks = ({
  noteId,
  summaryMarkdown,
}: {
  noteId: Identifier;
  summaryMarkdown: string;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const api = useIntegrationApi();
  const items = parseActionItems(summaryMarkdown);
  const [selected, setSelected] = useState<ReadonlySet<number>>(
    () => new Set(items.map((_, i) => i)),
  );
  const [done, setDone] = useState<ReadonlySet<number>>(() => new Set());
  const [busy, setBusy] = useState(false);
  if (items.length === 0) return null;
  const toggle = (i: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  const create = async () => {
    const indexes = items
      .map((_, i) => i)
      .filter((i) => selected.has(i) && !done.has(i));
    const chosen = indexes.map((i) => items[i]);
    if (chosen.length === 0) {
      notify("gavion.meetings.tasks.none_selected", { type: "warning" });
      return;
    }
    setBusy(true);
    try {
      const r = await api.granolaCreateTasks(noteId, chosen, null);
      notify("gavion.meetings.tasks.created", {
        type: "success",
        messageArgs: { smart_count: r.created },
      });
      setDone((prev) => new Set([...prev, ...indexes]));
      refresh();
    } catch (e) {
      notify(`gavion.meetings.errors.${errorCodeOf(e)}`, {
        type: "error",
        messageArgs: { _: translate("gavion.meetings.errors.unknown") },
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <section
      className="mt-2 rounded-lg border p-3"
      aria-label={translate("gavion.meetings.tasks.title")}
    >
      <h5 className="mb-2 text-sm font-semibold">
        {translate("gavion.meetings.tasks.title")}
      </h5>
      <ul className="flex flex-col gap-1">
        {items.map((text, i) => (
          <li key={i} className="flex items-center gap-2 text-sm">
            <Checkbox
              id={`task-${noteId}-${i}`}
              aria-label={text}
              checked={selected.has(i)}
              disabled={done.has(i)}
              onCheckedChange={() => toggle(i)}
            />
            <label htmlFor={`task-${noteId}-${i}`}>{text}</label>
          </li>
        ))}
      </ul>
      <Button
        className="mt-2"
        size="sm"
        onClick={create}
        disabled={
          busy || items.every((_, i) => done.has(i) || !selected.has(i))
        }
      >
        {translate("gavion.meetings.tasks.create")}
      </Button>
    </section>
  );
};
